import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorPrim,
  fontColorSecond,
  red,
  white,
} from '../../assets/styledAssets/vars';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import { DeleteIcon, SearchIcon } from '../../components/Icons/Icons.component';
import { GridColumn } from '@progress/kendo-react-grid';
import moment from 'moment';
import messages from './messages';
import { Input } from '@progress/kendo-react-inputs';
import {
  StyledCheckBox,
  StyledFormField,
} from '../../components/FormField/styles';
import { fetchUsers } from './Users.actions';
import debounce from 'lodash/debounce';
import { setSearch, deleteUser, setListUsers } from './Users.slice';
import { Code, Roles, User } from '../../domains/app';
import Button from '../../components/Button/Button.component';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import InviteMember from '../../components/InviteMember/InviteMember.component';
import SpaceService from '../../service/Space.service';
import PersonsService from '../../service/Persons.service';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import { ConfirmationBtnText } from '../../app/constants';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import { ImgWrap } from '../../components/SideBar/styles';
import { avatarLink } from '../../helpers/app.helpers';
import AccessCode from '../Groups/components/AccessCode/AccessCode.component';
import {
  createMembersCode,
  getSpaceCodes,
} from '../../service/Integration.service';

const connector = connect(
  (state: RootState) => ({
    listUsers: state.users.listUsers,
    search: state.users.search,
    isFetching: state.users.isFetching,
    total: state.users.total,
    pageSize: state.users.pageSize,
    selectedAccount: state.app.selectedAccount,
    profile: state.app.user,
    currentPlan: state.settings.currentPlan,
  }),
  {
    setSearch,
    fetchUsers,
    deleteUser,
    setListUsers,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Users: FunctionComponent<PropsFromRedux> = ({
  listUsers,
  search,
  isFetching,
  total,
  pageSize,
  setSearch,
  fetchUsers,
  selectedAccount,
  profile,
  currentPlan,
  deleteUser,
  setListUsers,
}) => {
  const intl = useIntl();
  const [skip, setSkip] = useState<number>(0);
  const [memeberInvitations, setMemeberInvitations] = useState<any[]>([]);
  const [isMembers] = useState<boolean>(
    window.location.pathname.endsWith('/members'),
  );
  const [showInvite, setShowInvite] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationDelete, setShowConfirmationDelete] = useState(false);
  const [confirmationHandlerDelete, setConfirmationHandlerDelete] = useState<
    () => void
  >();
  const [accessCodeDialog, setAccessCodeDialog] = useState(false);
  const [listCodes, setListCodes] = useState<Code[]>([]);

  useEffect(() => {
    fetchUsers(true);

    if (isMembers) {
      SpaceService.fetchListMemberInvitations(
        selectedAccount?.id as string,
        1000,
        0,
      ).then((res) => {
        setMemeberInvitations(res.items);
      });
    }

    if (isMembers) {
      getSpaceCodes(selectedAccount?.id as string).then((res) => {
        setListCodes(res);
      });
    }

    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchUsers(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= listUsers.length &&
        !isFetching &&
        total > listUsers.length
      ) {
        fetchUsers(false);
      }
    },
    [isFetching, listUsers.length, fetchUsers, total, setSkip, skip, pageSize],
  );

  const getListRoles = (role: number) => {
    let listRoles = '';
    if (role & Roles.User) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.user)}`
        : intl.formatMessage(messages.user);
    }
    if (role & Roles.SpaceOwner) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.spaceOwner)}`
        : intl.formatMessage(messages.spaceOwner);
    }
    if (role & Roles.Administrator) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.administrator)}`
        : intl.formatMessage(messages.administrator);
    }
    if (role & Roles.SpaceAdministrator) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.spaceAdministrator)}`
        : intl.formatMessage(messages.spaceAdministrator);
    }
    if (role & Roles.SiteOwner) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.siteOwner)}`
        : intl.formatMessage(messages.siteOwner);
    }
    return listRoles;
  };

  const generateCode = useCallback(
    (m: number) => {
      const membersIds = listUsers
        .filter((u) => u.checked)
        .map((u) => u.id) as string[];
      createMembersCode(selectedAccount?.id as string, membersIds, m).then(
        (code) => {
          const now = new Date();
          now.setMinutes(now.getMinutes() + m);
          setListCodes([
            ...listCodes,
            { id: code, expired: now, memberIds: membersIds },
          ]);
        },
      );
    },
    [setListCodes, listCodes, listUsers, selectedAccount?.id],
  );

  const inviteMember = (email: string) => {
    SpaceService.inviteMember(
      selectedAccount?.id as string,
      profile?.id as string,
      email,
    )
      .then((res) => {
        if (res?.id) {
          setMemeberInvitations((l) => {
            l.push(res);
            return [...l];
          });
        }
      })
      .finally(() => setShowInvite(false));
  };

  const deleteInvite = (dataItem: any | User) => {
    if (dataItem.invitedByPersonId) {
      SpaceService.deleteInvitation(dataItem.id)
        .then((res) => {
          setMemeberInvitations((l) => {
            return [...l.filter((i) => i.id !== dataItem.id)];
          });
        })
        .finally(() => setShowInvite(false));
    } else {
      SpaceService.leaveFromSpace(selectedAccount?.id as string, dataItem.id)
        .then((res) => {
          deleteUser(dataItem.id);
        })
        .finally(() => setShowInvite(false));
    }
  };

  const removeUser = (dataItem: any | User) => {
    setShowConfirmationDelete(true);
    setConfirmationHandlerDelete(() => {
      return () => {
        PersonsService.deleteUser(dataItem.id).then((res) => {
          deleteUser(dataItem.id);
        });
      };
    });
  };

  const updateField = (dataItem: any, field: keyof User) => {
    const index = listUsers.findIndex((m) => m.id === dataItem.id);
    let newMembers = [...listUsers];
    newMembers[index] = {
      ...newMembers[index],
      [field]: !dataItem[field],
    };
    setListUsers(newMembers);
  };

  return (
    <Wrap
      flexDir={'column'}
      flexWrap={'nowrap'}
      overflow={['auto', 'unset']}
      width={'100%'}
    >
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap
          alignItems={'center'}
          width={'100%'}
          justifyCont={'space-between'}
        >
          <Wrap
            alignItems={['flex-start', 'flex-end']}
            flexDir={['column', 'row']}
          >
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[
                [{ side: 'right', color: 'transparent' }],
                [{ side: 'right', color: accentColorSecond }],
              ]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 1rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(
                isMembers ? messages.members : messages.header,
              )}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {total + memeberInvitations.length}{' '}
              {intl.formatMessage(messages.usersTotal)}
            </Wrap>
          </Wrap>

          {isMembers && (
            <Wrap justifyCont={'flex-end'}>
              {!!listUsers.filter((m) => m.checked).length && (
                <Wrap margin={'0 2rem'}>
                  <Button primary onClick={() => setAccessCodeDialog(true)}>
                    {intl.formatMessage(messages.createAccessCode)}
                  </Button>
                </Wrap>
              )}
              <Button
                primary
                onClick={() => {
                  if (
                    currentPlan &&
                    currentPlan.subscriptionPlan.maxMemberCount <=
                      total + memeberInvitations.length
                  ) {
                    setShowConfirmation(true);
                  } else {
                    setShowInvite(true);
                  }
                }}
              >
                {intl.formatMessage(messages.invite)}
              </Button>
            </Wrap>
          )}
        </Wrap>
        <Wrap
          alignItems={'center'}
          margin={['2rem 0', '4rem 0 3rem 0']}
          maxWidth={'34rem'}
          width={'100%'}
        >
          <StyledFormField>
            <Input
              value={search}
              onChange={handleChangeInput}
              placeholder={intl.formatMessage(messages.search)}
            />
            <SearchIcon color={fontColorPrim} width={'2rem'} />
          </StyledFormField>
          {/*          <Column lg={70}>
            <Wrap justifyCont={'flex-end'}>
              <Button icon>
                <SettingsIcon width={'2rem'} color={accentColorSecond} />
              </Button>
            </Wrap>
          </Column>*/}
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        overflow={'auto'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable
          style={{ height: '100%' }}
          data={memeberInvitations
            .concat(listUsers)
            .slice(skip, skip + pageSize)}
          onPageChange={pageHandler}
          scrollable={'virtual'}
          skip={skip}
          pageSize={pageSize}
          total={total + memeberInvitations.length}
          rowHeight={75}
        >
          {isMembers && (
            <GridColumn
              field={'checked'}
              width="50px"
              headerCell={() => <div />}
              cell={(props) =>
                props.dataItem.invitedByPersonId ? (
                  <td></td>
                ) : (
                  <td
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      updateField(props.dataItem, 'checked');
                    }}
                  >
                    <StyledCheckBox
                      className={'field-checkbox'}
                      checked={props.dataItem.checked}
                    />
                  </td>
                )
              }
            />
          )}
          <GridColumn
            field="avatarId"
            title="Avatar"
            width="70px"
            cell={(props) => (
              <td>
                <ImgWrap
                  pic={avatarLink(props.dataItem?.avatarId as string)}
                  size="4rem"
                />
              </td>
            )}
          />
          <GridColumn field="firstName" title="FirstName" />
          <GridColumn field="lastName" title="LastName" />
          <GridColumn field="email" title="Email" />
          <GridColumn field="phone" title="Phone" width="150px" />
          {!isMembers && (
            <GridColumn
              field="role"
              title="Role"
              cell={(props) => <td>{getListRoles(props.dataItem.role)}</td>}
            />
          )}
          <GridColumn
            field="enrolled"
            title="Enrolled"
            width="150px"
            cell={(props) => (
              <td>
                {!!props.dataItem.enrolled &&
                  moment(props.dataItem.enrolled).format('DD MMM YYYY')}
              </td>
            )}
          />
          <GridColumn
            field="invited"
            title="Invited"
            width="150px"
            cell={(props) => (
              <td>
                {!!props.dataItem.invited &&
                  moment(props.dataItem.invited).format('DD MMM YYYY')}
              </td>
            )}
          />
          <GridColumn
            field="status"
            title="Status"
            width="150px"
            cell={(props) => (
              <td>
                {props.dataItem.invitedToSpaceId ? (
                  <Wrap color={red}>
                    {intl.formatMessage(messages.pending)}
                  </Wrap>
                ) : (
                  <Wrap color={accentColorPrim}>
                    {intl.formatMessage(messages.active)}
                  </Wrap>
                )}
              </td>
            )}
          />

          <GridColumn
            width={'40px'}
            cell={(props) => (
              <StyledTD>
                {isMembers && (
                  <Button
                    icon
                    transparent
                    onClick={(e: any) => {
                      e.preventDefault();
                      deleteInvite(props.dataItem);
                    }}
                  >
                    <DeleteIcon color={red} width={'2rem'} />
                  </Button>
                )}
                {!isMembers &&
                  !(
                    props.dataItem.role & Roles.Administrator ||
                    props.dataItem.role & Roles.SiteOwner ||
                    props.dataItem.role & Roles.SpaceOwner
                  ) && (
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeUser(props.dataItem);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  )}
              </StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>
      <Wrap width={'100%'} hiddenSM overflow={'auto'}>
        {listUsers.map((user: any, index) => (
          <Wrap
            flexDir={'column'}
            flexWrap={'wrap'}
            margin={'0 0 2rem 0'}
            width={'100%'}
            padding={'3rem'}
            br={'1.2rem'}
            bgColor={white}
            key={`card-${index}`}
          >
            <Wrap
              alignItems={'center'}
              justifyCont={'space-between'}
              width={'100%'}
              margin={'0 0 2rem 0'}
            >
              <Wrap fs={'2rem'} fw={'bold'}>
                {user.firstName} {user.lastName}
              </Wrap>
              <Wrap>
                {user.role
                  ? getListRoles(user.role)
                      .split(',')
                      .map((item, index) => (
                        <Wrap
                          margin={'.2rem'}
                          key={`item-${index}`}
                          color={white}
                          fs={'1.4rem'}
                          bgColor={accentColorPrim}
                          padding={'.3rem .5rem'}
                          br={'.5rem'}
                        >
                          {item}
                        </Wrap>
                      ))
                  : ''}
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'} margin={'0 0 2rem 0'}>
              <Wrap color={fontColorSecond} margin={'0 1.6rem 0 0'}>
                {intl.formatMessage(messages.enrolled)}
              </Wrap>
              <Wrap color={fontColorSecond}>
                {moment(user.enrolled).format('DD MMM YYYY')}
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
              <Wrap color={fontColorSecond}>{user.email}</Wrap>
            </Wrap>
            <Wrap alignItems={'center'}>
              <Wrap color={fontColorSecond}>{user.phone}</Wrap>
            </Wrap>
          </Wrap>
        ))}
        {memeberInvitations.map((member: any, index) => (
          <Wrap
            flexDir={'column'}
            flexWrap={'wrap'}
            margin={'0 0 2rem 0'}
            width={'100%'}
            padding={'3rem'}
            br={'1.2rem'}
            bgColor={white}
            key={`card-${index}`}
          >
            <Wrap
              alignItems={'center'}
              justifyCont={'space-between'}
              width={'100%'}
              margin={'0 0 2rem 0'}
            >
              <Wrap fs={'2rem'} fw={'bold'}>
                {member.firstName} {member.lastName}
              </Wrap>
              <Wrap>
                {member.invitedToSpaceId ? (
                  <Wrap
                    bgColor={red}
                    br={'1rem'}
                    padding={'.2rem .6rem'}
                    color={white}
                  >
                    {intl.formatMessage(messages.pending)}
                  </Wrap>
                ) : (
                  <Wrap
                    bgColor={accentColorPrim}
                    br={'1rem'}
                    padding={'.2rem .6rem'}
                    color={white}
                  >
                    {intl.formatMessage(messages.active)}
                  </Wrap>
                )}
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'} margin={'0 0 2rem 0'}>
              <Wrap color={fontColorSecond} margin={'0 1.6rem 0 0'}>
                {intl.formatMessage(messages.enrolled)}
              </Wrap>
              <Wrap color={fontColorSecond}>
                {moment(member.enrolled).format('MM/DD/YYYY')}
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
              <Wrap color={fontColorSecond}>{member.email}</Wrap>
            </Wrap>
            <Wrap alignItems={'center'}>
              <Wrap color={fontColorSecond}>{member.phone}</Wrap>
            </Wrap>
          </Wrap>
        ))}
      </Wrap>
      {/*      {dialogVisibility && (
        <DialogWrap
          title={
            isNewDialog
              ? intl.formatMessage(messages.addUser)
              : intl.formatMessage(messages.userDetails)
          }
          children={
            <AddEditUser
              onCancel={() => setDialogVisibility(!dialogVisibility)}
              data={dialogData}
            />
          }
        />
      )}*/}
      {showInvite && (
        <DialogWrap
          title={intl.formatMessage(messages.inviteMember)}
          children={
            <InviteMember
              onCancel={() => setShowInvite(false)}
              onSave={inviteMember}
            />
          }
        />
      )}
      {showConfirmation && (
        <ConfirmationDialog
          bodyText={intl.formatMessage(messages.membersLimit)}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={() => setShowConfirmation(false)}
          confirmBtnText={ConfirmationBtnText.OK}
          hideCancelBtn
        />
      )}
      {showConfirmationDelete && confirmationHandlerDelete && (
        <ConfirmationDialog
          bodyText={intl.formatMessage(messages.deleteUserConfirmation)}
          handleCancel={() => setShowConfirmationDelete(false)}
          handleConfirm={confirmationHandlerDelete}
        />
      )}
      {accessCodeDialog && (
        <DialogWrap
          children={
            <AccessCode
              onCancel={() => setAccessCodeDialog(false)}
              generateCode={generateCode}
              listCodes={listCodes.filter((l) =>
                listUsers
                  .filter((u) => u.checked)
                  .map((u) => u.id)
                  .every((id) => l.memberIds?.includes(id as string)),
              )}
            />
          }
        />
      )}
    </Wrap>
  );
};

export default connector(Users);
